import { API, deleteRequest, getRequest, patchRequest, postRequest, putRequest } from '@/api/api';
import type { SignedUpAs } from '@/store/auth/auth.dto';

import type {
  Connection,
  CrewMemberResponse,
  DeletePersonRolesRequest,
  GetConnectionsParams,
  PastCrewMember,
  Person,
  PersonDocument,
  PersonNote,
  PersonPresenceStatus,
  PersonRole,
  SendConnectionRequest,
  Subscription,
  SubscriptionPlan,
  SubscriptionPrice,
  UpdateReinviteRequest,
  VesselInviteResponse,
} from './person.dto';

export const assignRoleToPerson = async (role: PersonRole) => {
  await putRequest<void, PersonRole>(API.PEOPLE, '/roles', role);
};

export const deletePersonRoles = async (request: DeletePersonRolesRequest) => {
  await deleteRequest<void, DeletePersonRolesRequest>(API.PEOPLE, '/roles', request);
};

// PATCH /{id}/onboard → 204. Flips the persisted `onboarded` flag true. The optional body
// records what the person signed up as; the server keeps the first answer only.
export const onboardPerson = async (personId: string, signedUpAs?: SignedUpAs) => {
  await patchRequest<void, { signed_up_as: SignedUpAs } | undefined>(
    API.PEOPLE,
    `/${personId}/onboard`,
    signedUpAs ? { signed_up_as: signedUpAs } : undefined,
  );
};

// GET /crew → CrewMemberResponse[]. Active crew with documents, note, contacts.
export const getCrewList = async (): Promise<CrewMemberResponse[]> => {
  const { data } = await getRequest<CrewMemberResponse[]>(API.PEOPLE, '/crew');
  return data;
};

// GET /invites/vessel → VesselInviteResponse[]. Pending invites with documents.
export const getVesselInvites = async (): Promise<VesselInviteResponse[]> => {
  const { data } = await getRequest<VesselInviteResponse[]>(API.PEOPLE, '/invites/vessel');
  return data;
};

// GET /{id}/note → PersonNote. Returns the vessel-scoped note for a crew member.
export const getPersonNote = async (personId: string): Promise<PersonNote | null> => {
  try {
    const { data } = await getRequest<PersonNote>(API.PEOPLE, `/${personId}/note`);
    return data;
  } catch {
    return null;
  }
};

// PUT /{id}/note → PersonNote. Creates or updates the note.
export const upsertPersonNote = async (personId: string, text: string): Promise<PersonNote> => {
  const { data } = await putRequest<PersonNote, { text: string }>(API.PEOPLE, `/${personId}/note`, {
    text,
  });
  return data;
};

// DELETE /{id}/note → 204.
export const deletePersonNote = async (personId: string): Promise<void> => {
  await deleteRequest(API.PEOPLE, `/${personId}/note`);
};

// GET /{id}/documents → PersonDocument[].
export const getPersonDocuments = async (personId: string): Promise<PersonDocument[]> => {
  const { data } = await getRequest<PersonDocument[]>(API.PEOPLE, `/${personId}/documents`);
  return data;
};

// POST /{id}/documents → PersonDocument[]. Uploads one or more files.
export const uploadPersonDocument = async (
  personId: string,
  file: File,
): Promise<PersonDocument[]> => {
  const formData = new FormData();
  formData.append('documents', file);
  const { data } = await postRequest<PersonDocument[], FormData>(
    API.PEOPLE,
    `/${personId}/documents`,
    formData,
  );
  return data;
};

/**
 * PATCH /{id}/documents/{document_id} → PersonDocument.
 *
 * Sets a certificate's expiry date, or clears it with `null` — the only thing
 * this endpoint writes. The upload above takes files and nothing else, so a
 * date always arrives here, whether it was chosen at upload time or changed
 * afterwards. RFC 3339 on the wire: the field is a `*time.Time` server-side.
 */
export const updatePersonDocumentExpiry = async (
  personId: string,
  documentId: string,
  expirationDate: null | string,
): Promise<PersonDocument> => {
  const { data } = await patchRequest<PersonDocument, { expiration_date: null | string }>(
    API.PEOPLE,
    `/${personId}/documents/${documentId}`,
    { expiration_date: expirationDate },
  );
  return data;
};

// DELETE /{id}/documents?ids=… → 204.
export const deletePersonDocuments = async (personId: string, ids: string[]): Promise<void> => {
  await deleteRequest(API.PEOPLE, `/${personId}/documents`, undefined, false, { ids });
};

// GET /past-crew → PastCrewMember[]. Returns past crew snapshots for the active vessel.
export const getPastCrew = async (): Promise<PastCrewMember[]> => {
  const { data } = await getRequest<PastCrewMember[]>(API.PEOPLE, '/past-crew');
  return data;
};

// DELETE /past-crew/{id} → 204. Removes a past crew snapshot.
export const deletePastCrewMember = async (id: string): Promise<void> => {
  await deleteRequest(API.PEOPLE, `/past-crew/${id}`);
};

// PATCH /{id}/presence → 204. Sets on_board | on_leave | off_board for this vessel member.
// When presence is on_leave, optional leave_start / leave_end (ISO strings) are forwarded.
// On on_board / off_board the two keys are omitted — the server clears them.
export const setPersonPresence = async (
  personId: string,
  presence: PersonPresenceStatus,
  dates?: { leave_start?: string; leave_end?: string },
) => {
  const body: Record<string, unknown> = { presence };
  if (presence === 'on_leave' && dates) {
    if (dates.leave_start) body.leave_start = dates.leave_start;
    if (dates.leave_end) body.leave_end = dates.leave_end;
  }
  await patchRequest<void, typeof body>(API.PEOPLE, `/${personId}/presence`, body);
};

export const getSubscriptions = async () => {
  const response = await getRequest<Subscription[]>(API.PEOPLE, '/subscriptions');
  return response.data;
};

export const createSubscriptionCheckout = async (plan: SubscriptionPlan): Promise<string> => {
  const response = await postRequest<{ url: string }, { lookup_key: SubscriptionPlan }>(
    API.PEOPLE,
    '/subscriptions/checkout',
    {
      lookup_key: plan,
    },
  );
  return response.data.url;
};

export const createSubscriptionPortal = async (): Promise<string> => {
  const response = await postRequest<{ url: string }, void>(
    API.PEOPLE,
    '/subscriptions/portal',
    undefined,
  );
  return response.data.url;
};

export const getSubscriptionPrices = async (lookupKeys: string[]) => {
  const response = await getRequest<SubscriptionPrice[]>(
    API.PEOPLE,
    '/subscriptions/prices',
    undefined,
    {
      lookup_keys: lookupKeys,
    },
  );
  return response.data;
};

export const getPerson = async (personId: string) => {
  const { data } = await getRequest<Person>(API.PEOPLE, `/${personId}`);
  return data;
};

export const getPeopleByIds = async (ids: string[]) => {
  const response = await getRequest<Person[]>(API.PEOPLE, '/ids', undefined, {
    ids: [...new Set(ids)],
  });
  return response.data;
};

export const sendConnection = async (data: SendConnectionRequest): Promise<Connection> => {
  const response = await postRequest<Connection, SendConnectionRequest>(
    API.PEOPLE,
    '/connections',
    data,
  );
  return response.data;
};

export const getMyConnections = async (params?: GetConnectionsParams): Promise<Connection[]> => {
  const response = await getRequest<{ items: Connection[] }>(
    API.PEOPLE,
    '/connections/my',
    undefined,
    { limit: 1000, page: 1, ...params },
  );
  return response.data.items;
};

export const getPendingConnections = async (
  params?: GetConnectionsParams,
): Promise<Connection[]> => {
  const response = await getRequest<{ items: Connection[] }>(
    API.PEOPLE,
    '/connections/pending',
    undefined,
    { limit: 1000, page: 1, ...params },
  );
  return response.data.items;
};

export const acceptConnection = async (id: string): Promise<void> => {
  await putRequest<void, void>(API.PEOPLE, `/connections/${id}/accept`, undefined);
};

export const declineConnection = async (id: string): Promise<void> => {
  await putRequest<void, void>(API.PEOPLE, `/connections/${id}/decline`, undefined);
};

export const updateReinvite = async (id: string, data: UpdateReinviteRequest): Promise<void> => {
  await patchRequest<void, UpdateReinviteRequest>(API.PEOPLE, `/connections/${id}/reinvite`, data);
};

export const deleteConnection = async (id: string): Promise<void> => {
  await deleteRequest<void>(API.PEOPLE, `/connections/${id}`);
};

export const getConnectionsByUserId = async (
  userId: string,
  params?: GetConnectionsParams,
): Promise<Connection[]> => {
  const response = await getRequest<{ items: Connection[] }>(
    API.PEOPLE,
    `/connections/user/${userId}`,
    undefined,
    { limit: 1000, page: 1, ...params },
  );
  return response.data.items;
};

export const getRolesOrdering = async () => {
  const response = await getRequest<{ service_id: string; order: number }[]>(
    API.PEOPLE,
    '/roles-ordering',
  );
  return response.data;
};

export const updateRolesOrdering = async (items: { service_id: string; order: number }[]) => {
  await putRequest(API.PEOPLE, '/roles-ordering', {
    items,
  });
};
